import { useCallback, useEffect, useMemo, useState } from "react";
import { SMSInfo } from "../models/polymer/Properties.model";
import { useGetFlatTypeApiByHdbRefQuery } from "../redux/services/getFlatType";
import { useGetLoanStatusApiByHdbRefQuery } from "../redux/services/getLoanStatus";
import { useGetHoldingTypeApiByHdbRefQuery } from "../redux/services/getHoldingType";
import { FlatTypeInfo } from "../models/polymer/FlatTypeInfo.model";
import { LoanStatusInfo } from "../models/polymer/LoanStatusInfo.model";
import { HoldingTypeInfo } from "../models/polymer/HoldingTypeInfo.model";
import { useAppSelector } from "../redux/hook";
import { useGetOutstandinLoanByHdbRefQuery } from "../redux/services/getOutstandingLoan";
import { useGetCheckApplicationHasInitiatedByHdbRefQuery } from "../redux/services/getCheckApplicationHasInitiatedByHdbRef";
import { PolymerAxios } from "../utils/axios";
import { API_EligibilityDebugLog } from "../utils/api";


function useCheckSmdByHdbRef(hdb_ref?:string, stage?: "A" | "B" | "C" | "D" | "E") {

    const [ hdbRefValue, setHdbRefValue ] = useState<string>("");
    const [ smsInfo, setSMSInfo ] = useState<SMSInfo | undefined>(undefined);
    const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
    const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);

    const { data: flatTypeData, isError: isFlatTypeError, error: flatTypeError,refetch: refetchFlatType, isUninitialized: isUninitializedFlatType, isFetching: isFlatTypeLoading } = useGetFlatTypeApiByHdbRefQuery({hdb_ref: hdbRefValue, uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? ""}, {skip: !hdbRefValue || (stage !== undefined && stage !== "A")});
    const { data: loanStatusData, isError: isLoanStatusError, error: loanStatusError, refetch: refetchLoanStatus, isUninitialized: isUninitializedLoanStatus, isFetching: isLoanStatusLoading } = useGetLoanStatusApiByHdbRefQuery({hdb_ref: hdbRefValue, uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? ""},{skip: !hdbRefValue || (stage !== undefined && stage > "C")});
    const { data: holdingTypeData, isError: isHoldingTypeError, error: holdingTypeError, refetch: refetchHoldingTypeData, isUninitialized: isUninitializedHoldingType, isFetching: isHoldingTypeLoading } = useGetHoldingTypeApiByHdbRefQuery({hdb_ref: hdbRefValue, uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? ""}, {skip: !hdbRefValue || (stage !== undefined && stage !== "A")});
    const { data: outstandingLoanData, isError: isOutstandingLoanError, error: outstandingLoanError, refetch: refetchOutstandingLoan, isUninitialized: isUninitializedOutstandingLoan, isFetching: isOutstandingLoanLoading } = useGetOutstandinLoanByHdbRefQuery({hdb_ref: hdbRefValue, uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? ""}, {skip: !hdbRefValue || (stage !== undefined && stage !== "A")});
    const { data: applicationInitiatedData, isError: isApplicationInitiatedError ,refetch: refetchApplicationInitiated, isUninitialized: isUninitializedApplicationInitiated, isFetching: applicationInitiatedLoading } = useGetCheckApplicationHasInitiatedByHdbRefQuery({hdb_ref: hdbRefValue, uinfin: loginUser?.uinfin ?? "" , singpass_token: singpassToken ?? ""}, {skip: !hdbRefValue || (stage !== undefined && stage !== "A")});

    const [ flatTypeInfo, setFlatTypeInfo ] = useState<FlatTypeInfo>();
    const [ loanStatusInfo, setLoanStatusInfo ] = useState<LoanStatusInfo>();
    const [ holdingTypeInfo, setHoldingTypeInfo ] = useState<HoldingTypeInfo>();
    const [ hasOutstandingFees, setHasOutstandingFees ] = useState<boolean>(false);
    const [ hasInitiatedApplication, setHasInitiatedApplication ] = useState<boolean>(false);
    const [ outstandingFeesError, setOutstandingFeesError ] = useState<boolean>();

    const isLoading = useMemo(() => {
      return isFlatTypeLoading || isLoanStatusLoading || isHoldingTypeLoading || isOutstandingLoanLoading || applicationInitiatedLoading;
    }, [isFlatTypeLoading, isLoanStatusLoading, isHoldingTypeLoading, isOutstandingLoanLoading, applicationInitiatedLoading])

    const isError = useMemo(() => {
      return isFlatTypeError || isLoanStatusError || isHoldingTypeError || isOutstandingLoanError || isApplicationInitiatedError;
    }, [isFlatTypeError, isLoanStatusError, isHoldingTypeError, isOutstandingLoanError, isApplicationInitiatedError])

    useEffect(()=>{
      setFlatTypeInfo(flatTypeData);
    }, [flatTypeData]);
    
    useEffect(()=>{
      setHasInitiatedApplication(applicationInitiatedData?.existed ?? false);
    }, [applicationInitiatedData]);

    useEffect(()=>{
      setLoanStatusInfo(loanStatusData);
    }, [loanStatusData]);

    useEffect(()=>{
      setHoldingTypeInfo(holdingTypeData);
    }, [holdingTypeData]);

    useEffect(()=>{
      setOutstandingFeesError(outstandingLoanData?.outstanding_fees);
    }, [outstandingLoanData]);

    useEffect(()=>{
      setHasOutstandingFees(!!outstandingLoanData?.outstanding_fees)
    },[outstandingLoanData?.outstanding_fees])

    useEffect(()=>{
      if(hdb_ref){
        setHdbRefValue(hdb_ref);
      }
    },[hdb_ref])


    useEffect(()=>{
      let checkNoData = false;
      if(stage === "A") {
        checkNoData = !flatTypeData || !loanStatusData || !holdingTypeData;
      } else if(stage === "B" || stage === "C") {
        checkNoData = !loanStatusData;
      }
      if(isLoading || checkNoData){
        return;
      }
      const smsInfo = new SMSInfo();
      if(flatTypeData) {
        smsInfo.setFlatType(flatTypeData);
      }

      if(loanStatusData) {
        smsInfo.setFinancingInfo(loanStatusData);
        smsInfo.setEligibilitySchemeCode(loanStatusData.eligibility_scheme_code);
      }
   
      if(holdingTypeData) {
        smsInfo.setHoldingType(holdingTypeData);
      }
      setSMSInfo(smsInfo.json());
    },[flatTypeData, loanStatusData, holdingTypeData, isLoading, stage])

    const setHdbRefValueFunction = useCallback((hdb_ref:string)=>{
      setHdbRefValue(hdb_ref);
    },[])

    const sendErrorLogs = useCallback((message: string, apiUrl: string, error: any) => {
      const payload = {
        application_uuid: hdbRefValue,
        message: JSON.stringify({
          uinfin: loginUser?.uinfin,
          hdb_ref: hdbRefValue,
          error_message: message,
          generic_error_message: "Frontend Interface Data Error",
          api_url: apiUrl,
          error: error ? JSON.stringify(error) : "cannot find error trace"
       })
      }
      PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, payload)
    }, [loginUser?.uinfin, singpassToken, hdbRefValue])

    useEffect(() => {
      if(isFlatTypeError){
        sendErrorLogs("Flat Type Api Error", "/getFlatType", flatTypeError);
      }
    }, [isFlatTypeError])

    useEffect(() => {
      if(isLoanStatusError){
        sendErrorLogs("Loan Status Api Error", "/getLoanStatus", loanStatusError);
      }
    }, [isLoanStatusError])

    useEffect(() => {
      if(isHoldingTypeError){
        sendErrorLogs("Holding Type Api Error", "/sharehldDetails", holdingTypeError);
      }
    }, [isHoldingTypeError])

    useEffect(() => {
      if(isOutstandingLoanError){
        sendErrorLogs("Outstanding Loan Api Error", "/api/v1/hdb/pfs/checkOutstandingLoan", outstandingLoanError);
      }
    }, [isOutstandingLoanError])

    useEffect(() => {
      if(!isUninitializedFlatType){
        refetchFlatType();
      }
  
      if(!isUninitializedLoanStatus){
        refetchLoanStatus();
      }
  
      if(!isUninitializedHoldingType){
        refetchHoldingTypeData();
      }
  
      if(!isUninitializedOutstandingLoan){
        refetchOutstandingLoan();
      }
  
      if(!isUninitializedApplicationInitiated){
        refetchApplicationInitiated();
      }
     }, [hdbRefValue])

    return { smsInfo, flatTypeInfo, loanStatusInfo, holdingTypeInfo, hasInitiatedApplication, isLoading, isError, hasOutstandingFees, setHdbRefValueFunction }
  
  
}

export default useCheckSmdByHdbRef
